import '../ManageLessons.scss';
import { Input, Modal, Select } from '@trackman/web-shared-components';
import { ReactComponent as Plus } from 'images/icons/Plus.svg';
import { ReactComponent as Pencil } from 'images/icons/Pencil.svg';
import { useState } from 'react';
import { AdminLesson, AdminLessonType, LocalizedImageReference } from 'types/admin/modulesV2';
import { VideoLessonField } from '../LessonTypeSpecificFields/VideoLessonField';
import { PictureLessonField } from '../LessonTypeSpecificFields/PictureLessonField';
import { TextLessonField } from '../LessonTypeSpecificFields/TextLessonField';
import { CalculatorLesson, CalculatorLessonField } from '../LessonTypeSpecificFields/CalculatorLessonField';
import { ChapterTestField } from '../LessonTypeSpecificFields/ChapterTestField';
import { getCalcOptionsByType } from 'utilities/adminCalculatorOptions';
import { CalculatorKind } from 'types/calculator/calculatorType';
import { AdminMedia } from 'types/admin/media';

type Props = {
  open: boolean;
  onClose: (payload: AdminLesson | undefined, localizedImages: LocalizedImageReference[]) => void;
  editedLesson: AdminLesson | undefined;
  localizedImages: LocalizedImageReference[];
};

type EditableLessonType = Exclude<AdminLessonType, 'Test'>;
type LessonOption = EditableLessonType | 'ChapterTest';

const SOURCE_LANG = 'en-US';

export const EditLessonDialog = ({ open, onClose, editedLesson, localizedImages }: Props) => {
  const defaultCalculatorValue = {
    calculatorKind: 'CALC_NUM' as CalculatorKind,
    calculatorOptions: getCalcOptionsByType('CALC_NUM'),
  };
  const [lessonTitle, setLessonTitle] = useState<string>(editedLesson?.title ?? '');
  const [lessonType, setLessonType] = useState<LessonOption>(
    !editedLesson ? 'Picture' : editedLesson.kind === 'Test' ? 'ChapterTest' : editedLesson.kind
  );
  const [pictureLessonImages, setPictureLessonImages] = useState<AdminMedia[] | undefined>(
    editedLesson?.kind === 'Picture' ? getPictureLessonImages(editedLesson, localizedImages) : undefined
  );
  const [pictureLessonDescription, setPictureLessonDescription] = useState<string | undefined>(
    editedLesson?.kind === 'Picture' ? editedLesson.text : undefined
  );
  const [videoLessonContent, setVideoLessonContent] = useState<string | undefined>(
    editedLesson?.kind === 'Video' ? editedLesson.youTubeKey : undefined
  );
  const [textLessonContent, setTextLessonContent] = useState<string | undefined>(editedLesson?.kind === 'Text' ? editedLesson.text : undefined);
  const [calculatorLessonContent, setCalculatorLessonContent] = useState<CalculatorLesson>(
    editedLesson?.kind === 'Calculator'
      ? {
          calculatorKind: editedLesson.calculatorKind as CalculatorKind,
          calculatorOptions: editedLesson.calculatorOptions as string,
        }
      : defaultCalculatorValue
  );
  const [chapterTestContent, setChapterTestContent] = useState<string | undefined>(
    editedLesson?.kind === 'Test' ? editedLesson.enterpoint : undefined
  );
  const [chapterTestId, setChapterTestId] = useState<string | undefined>(editedLesson?.kind === 'Test' ? editedLesson.id : undefined);

  const lessonOptions: Record<LessonOption, string> = {
    Picture: 'Image',
    Video: 'Video',
    Text: 'Text',
    Calculator: 'Calculator',
    ChapterTest: 'Chapter Test',
  };

  const handleAddLesson = () => {
    if (lessonType === 'ChapterTest') {
      createChapterTest();
    } else {
      createLesson(lessonType);
    }
  };

  const createChapterTest = () => {
    const newChapterTest: AdminLesson = {
      id: chapterTestId || crypto.randomUUID(),
      title: lessonTitle,
      kind: 'Test',
      enterpoint: chapterTestContent,
    };
    resetLocalState();
    onClose(newChapterTest, localizedImages);
  };

  const shouldDisableAddBtn = !lessonTitle;

  const createLesson = (type: EditableLessonType) => {
    const newLessonBase: Pick<AdminLesson, 'id' | 'title' | 'kind'> = {
      id: editedLesson?.id ?? crypto.randomUUID(),
      title: lessonTitle,
      kind: type,
    };
    let newLesson: AdminLesson;
    let nextLocalizedImages = type === 'Picture' ? localizedImages : removePreviousPictureImages(localizedImages, editedLesson);

    if (type === 'Calculator') {
      newLesson = {
        ...newLessonBase,
        calculatorKind: calculatorLessonContent?.calculatorKind,
        calculatorOptions: calculatorLessonContent?.calculatorOptions,
      };
    } else if (type === 'Picture') {
      const pictureImages = mapPictureLessonImages(pictureLessonImages, localizedImages, editedLesson);
      nextLocalizedImages = pictureImages.localizedImages;
      newLesson = {
        ...newLessonBase,
        medias: pictureImages.medias,
        text: pictureLessonDescription,
      };
    } else if (type === 'Video') {
      newLesson = {
        ...newLessonBase,
        youTubeKey: videoLessonContent,
      };
    } else {
      newLesson = {
        ...newLessonBase,
        text: textLessonContent,
      };
    }
    resetLocalState();
    onClose(newLesson, nextLocalizedImages);
  };

  const handleClose = () => {
    resetLocalState();
    onClose(undefined, localizedImages);
  };

  const resetLocalState = () => {
    setCalculatorLessonContent(defaultCalculatorValue);
    setPictureLessonImages([]);
    setPictureLessonDescription('');
    setVideoLessonContent('');
    setTextLessonContent('');
    setChapterTestContent('');
    setLessonTitle('');
    setLessonType('Picture');
  };

  return (
    <Modal
      className='edit-lesson-modal'
      cancelBtn={{
        label: 'Cancel',
        onClick: handleClose,
      }}
      confirmBtn={{
        label: editedLesson ? 'Edit' : 'Add',
        onClick: handleAddLesson,
        disabled: shouldDisableAddBtn,
      }}
      onClose={handleClose}
      title={editedLesson ? 'Edit Lesson' : 'Add Lesson'}
      titleIcon={editedLesson ? <Pencil /> : <Plus />}
      open={open}
    >
      <div className='edit-lesson-content'>
        <Input
          label='Title'
          value={lessonTitle}
          required
          onChange={(e) => setLessonTitle(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        />

        <Select
          label='Choose an option'
          value={lessonType}
          required
          selectOptions={Object.entries(lessonOptions).map(([key, value]) => {
            return {
              label: value,
              value: key,
            };
          })}
          onChange={(e) => setLessonType(e.target.value as LessonOption)}
        />

        {lessonType === 'Video' && (
          <VideoLessonField lessonContent={videoLessonContent as string} handleLessonContent={setVideoLessonContent} />
        )}
        {lessonType === 'Picture' && (
          <PictureLessonField
            images={pictureLessonImages as AdminMedia[]}
            description={pictureLessonDescription as string}
            handleImages={setPictureLessonImages}
            handleDescription={setPictureLessonDescription}
          />
        )}
        {lessonType === 'Text' && (
          <TextLessonField lessonContent={textLessonContent as string} handleLessonContent={setTextLessonContent} />
        )}
        {lessonType === 'Calculator' && (
          <CalculatorLessonField lessonContent={calculatorLessonContent} handleLessonContent={setCalculatorLessonContent} />
        )}
        {lessonType === 'ChapterTest' && (
          <ChapterTestField
            lessonContent={chapterTestContent as string}
            id={chapterTestId as string}
            handleLessonContent={setChapterTestContent}
            handleLessonId={setChapterTestId}
          />
        )}
      </div>
    </Modal>
  );
};

const removePreviousPictureImages = (localizedImages: LocalizedImageReference[], editedLesson: AdminLesson | undefined): LocalizedImageReference[] => {
  const previousSourceMediaId = editedLesson?.medias?.[0];
  if (!previousSourceMediaId) {
    return localizedImages;
  }

  return localizedImages.filter((ref) => ref.sourceImageId !== previousSourceMediaId);
};

const getPictureLessonImages = (lesson: AdminLesson, localizedImages: LocalizedImageReference[]): AdminMedia[] => {
  const sourceMediaId = lesson.medias?.[0];
  if (!sourceMediaId) {
    return [];
  }

  return [
    { countryCode: SOURCE_LANG, mediaId: sourceMediaId } as AdminMedia,
    ...localizedImages
      .filter((ref) => ref.sourceImageId === sourceMediaId)
      .map((ref) => ({ countryCode: ref.culture, mediaId: ref.localizedImageId } as AdminMedia)),
  ];
};

const mapPictureLessonImages = (
  images: AdminMedia[] | undefined,
  localizedImages: LocalizedImageReference[],
  editedLesson: AdminLesson | undefined
): { medias: string[] | undefined; localizedImages: LocalizedImageReference[] } => {
  const sourceMediaId = images?.find((image) => image.countryCode === SOURCE_LANG)?.mediaId;
  const previousSourceMediaId = editedLesson?.medias?.[0];

  let nextLocalizedImages = localizedImages.filter(
    (ref) => (!sourceMediaId || ref.sourceImageId !== sourceMediaId) && (!previousSourceMediaId || ref.sourceImageId !== previousSourceMediaId)
  );

  if (!sourceMediaId) {
    return { medias: undefined, localizedImages: nextLocalizedImages };
  }

  const newRefs = (images ?? [])
    .filter((image) => image.countryCode !== SOURCE_LANG)
    .map((image) => ({ sourceImageId: sourceMediaId, culture: image.countryCode, localizedImageId: image.mediaId }));

  nextLocalizedImages = [...nextLocalizedImages, ...newRefs];

  return { medias: [sourceMediaId], localizedImages: nextLocalizedImages };
};
